import React, { useEffect, useMemo, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import { isMobile } from "react-device-detect"
import axios from "axios"
import { Trophy, Medal, ListChecks, Star, Search, X } from "lucide-react"
import "./LeaderboardPodium.css"
import Logo from "../../assets/images/brands/converge.png"


// ── Score / quiz aggregation ────────────────────────────────────────────────
// Each person's `scores` array holds one entry per scoring event — quiz
// attempts, sessions, feedback, etc. — plus one entry with gname exactly
// "Score" that the backend already keeps as the running total of
// everything else on the document. Per spec:
//   • Score column  = read straight off that "Score" entry, as-is.
//   • Quiz column   = sum of just the entries whose gname starts with
//                     "Quiz_" (a subset of what's folded into "Score").
// Ranking is by the Score column, since that's the overall total.
const QUIZ_PREFIX = "Quiz_"
const isQuizEntry = gname => gname?.startsWith(QUIZ_PREFIX)

const humanizeQuizName = gname =>
  gname
    .slice(QUIZ_PREFIX.length)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const buildEntries = docs =>
  (docs || [])
    .map(doc => {
      const scores = doc.scores || []
      const quizEntries = scores.filter(s => isQuizEntry(s?.gname))
      const quizTotal = quizEntries.reduce((sum, s) => sum + (Number(s.score) || 0), 0)
      const scoreEntry = scores.find(s => s?.gname === "Score")
      const totalScore = scoreEntry ? Number(scoreEntry.score) || 0 : 0
      return {
        id: doc.id,
        name: doc.name || "Player",
        totalScore,
        quizTotal,
        quizCount: quizEntries.length,
        quizTitles: quizEntries.map(s => humanizeQuizName(s.gname)),
      }
    })
    // Only people the backend has actually scored show up on the board.
    .filter(entry => entry.totalScore > 0)
    .sort((a, b) => b.totalScore - a.totalScore || a.name.localeCompare(b.name))
    .map((entry, idx) => ({ ...entry, rank: idx + 1 }))

const initials = name =>
  (name || "")
    .trim()
    .split(/\s+/)
    .map(w => w[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("")

/* ── PODIUM (commented out, kept for later) ─────────────────────────────────
// 1st gets a trophy, 2nd/3rd get a medal, tinted gold/silver/bronze by the
// CSS on .lb-podium-avatar-icon (see LeaderboardPodium.css) — no avatar
// background, just the icon itself, enlarged.
const PODIUM_ICON = { 1: Trophy, 2: Medal, 3: Medal }

const PodiumAvatar = ({ position }) => {
  const Icon = PODIUM_ICON[position]
  return (
    <div className="lb-podium-avatar">
      <Icon className="lb-podium-avatar-icon" />
    </div>
  )
}
─────────────────────────────────────────────────────────────────────────── */

const normalizeId = v => (v ?? "").toString().trim().toLowerCase()

const LeaderboardPodium = () => {
  const history = useHistory()
  const goBackToLobby = () => history.push("/page/lobby")

  const obj = JSON.parse(localStorage.getItem("authUser") || "{}")
  const currentUserId = normalizeId(obj?.Email || obj?.EmailAddress || obj?._id)

  const [entries, setEntries] = useState([])
  const [status, setStatus] = useState("loading") // loading | loaded | error
  const [query, setQuery] = useState("")

  const hasFetched = useRef(false)

  const adminPanel = isMobile
    ? `${process.env.REACT_APP_ADMIN}`
    : `${process.env.REACT_APP_ADMIN_LAPTOP}`

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    axios
      .get(`${adminPanel}/getscores`)
      .then(res => {
        setEntries(buildEntries(res?.data))
        setStatus("loaded")
      })
      .catch(err => {
        console.error("Failed to fetch leaderboard:", err)
        setStatus("error")
      })
  }, [])

  const isMe = e => !!currentUserId && normalizeId(e.id) === currentUserId

  // Pinned summary card up top — the logged-in user's own rank/quiz/score,
  // so they don't have to hunt for themselves in the table.
  const me = useMemo(() => entries.find(isMe) || null, [entries, currentUserId])

  // The table itself now shows EVERY ranked player, including "you" — in
  // their true rank slot, just highlighted gold. Search filters this list
  // live, letter by letter, by name; ranks shown are always the real
  // overall ranks, not the filtered position.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter(e => e.name.toLowerCase().includes(q))
  }, [entries, query])

  /* ── PODIUM slots (commented out, kept for later) ──
  const top3 = entries.slice(0, 3)
  const rest = entries.slice(3)
  const podiumSlots = [
    { pos: 2, entry: top3[1] },
    { pos: 1, entry: top3[0] },
    { pos: 3, entry: top3[2] },
  ]
  ── */

  return (
    <div className="lb-page">
      <div className="feedback-page-header">
        <button className="fb-back-link" onClick={goBackToLobby}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"
            fill="white" viewBox="0 0 24 24" >
            <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8"></path><path d="m12.71 8.71-1.42-1.42L6.59 12l4.7 4.71 1.42-1.42-2.3-2.29H17v-2h-6.59z"></path>
          </svg>
        </button>
        <span className="fb-page-title">Leaderboard</span>
      </div>

      <div className="lb-main">
        <div className="lb-card-wrapper">
          <div className="lb-card">
            <div className="lb-card-inner">
              {status === "loading" && (
                <div className="lb-status">
                  <div className="lb-spinner" />
                  <span>Loading leaderboard…</span>
                </div>
              )}
              {status === "error" && (
                <div className="lb-status lb-status--error">
                  Could not load the leaderboard. Please try again shortly.
                </div>
              )}
              {status === "loaded" && entries.length === 0 && (
                <div className="lb-status">No quiz scores yet — be the first!</div>
              )}

              {status === "loaded" && entries.length > 0 && (
                <>
                  {/* ══ YOU — pinned above the table, never scrolls ══ */}
                  {me && (
                    <div className="lb-you-card">
                      <div className="lb-you-star">
                        <Star fill="currentColor" strokeWidth={1.5} />
                      </div>
                      <div className="lb-you-info">
                        <span className="lb-you-label">Your standing</span>
                        <span className="lb-you-name">{me.name}</span>
                      </div>
                      <div className="lb-you-stats">
                        <div className="lb-you-stat">
                          <span className="lb-you-stat-val">#{me.rank}</span>
                          <span className="lb-you-stat-lbl">Rank</span>
                        </div>
                        <div className="lb-you-stat">
                          <span className="lb-you-stat-val lb-you-stat-val--quiz">{me.quizTotal}</span>
                          <span className="lb-you-stat-lbl">Quiz</span>
                        </div>
                        <div className="lb-you-stat">
                          <span className="lb-you-stat-val lb-you-stat-val--score">{me.totalScore}</span>
                          <span className="lb-you-stat-lbl">Score</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══ PODIUM (commented out, kept for later) ══
                  <div className="lb-intro-spacer" aria-hidden="true" />
                  <section className="lb-podium-section" aria-label="Top 3 players">
                    {podiumSlots.map(({ pos, entry }) => (
                      <div key={pos} className="lb-podium-col" data-position={pos}>
                        {entry && (
                          <div className="lb-podium-player">
                            <PodiumAvatar position={pos} />
                            <span className="lb-podium-name">{entry.name}</span>
                            <span className="lb-podium-score">{entry.totalScore} pts</span>
                          </div>
                        )}
                        <div className="lb-podium-block">
                          <span className="lb-podium-num">{pos}</span>
                        </div>
                      </div>
                    ))}
                  </section>
                  ══ */}

                  {/* ══ SEARCH — filters the table below in real time, on
                      every keystroke ══ */}
                  <div className="lb-search">
                    <Search className="lb-search-icon" size={15} />
                    <input
                      type="text"
                      className="lb-search-input"
                      placeholder="Search a name…"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      autoComplete="off"
                    />
                    {query && (
                      <button
                        type="button"
                        className="lb-search-clear"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* ══ TABLE — every ranked player, starting from 1st
                      place. "You" appear here too, at your true rank, with
                      a gold gradient row so you're easy to spot without
                      losing your place in the overall order. Only this
                      part scrolls. ══ */}
                  <div className="lb-table-wrap">
                    <div className="lb-table-scroll">
                      <table className="lb-table">
                        <thead>
                          <tr>
                            <th className="lb-col-rank">Rank</th>
                            <th>Name</th>
                            {/* <th>Quiz</th> */}
                            <th className="lb-col-score">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visible.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="lb-empty-rest">
                                {query
                                  ? `No one matches "${query}".`
                                  : "No one on the board so far."}
                              </td>
                            </tr>
                          ) : (
                            visible.map((entry, idx) => {
                              const mine = isMe(entry)
                              const classes = [
                                entry.rank <= 3 ? `lb-row-top lb-row-top--${entry.rank}` : null,
                                mine ? "lb-row-me" : null,
                              ]
                                .filter(Boolean)
                                .join(" ") || undefined

                              return (
                                <tr
                                  key={entry.id}
                                  className={classes}
                                  style={{ "--i": Math.min(idx, 14) }}
                                >
                                  <td className="lb-col-rank">
                                    <span className={`lb-rank-chip lb-rank-chip--${entry.rank <= 3 ? entry.rank : "n"}`}>
                                      {entry.rank}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="lb-row-name">
                                      {/* <span className="lb-row-avatar">{initials(entry.name)}</span> */}
                                      <span>{entry.name}</span>
                                      {mine && (
                                        <span className="lb-you-tag">
                                          <Star size={10} fill="currentColor" strokeWidth={0} />
                                          YOU
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {/* <td>
                                    <span
                                      className={`lb-quiz-badge${
                                        entry.quizTotal === 0 ? " lb-quiz-badge-none" : ""
                                      }`}
                                      title={
                                        entry.quizTitles.length
                                          ? entry.quizTitles.join(", ")
                                          : "No quizzes played"
                                      }
                                    >
                                      <ListChecks size={11} />
                                      {entry.quizTotal} pts
                                    </span>
                                  </td> */}
                                  <td className="lb-col-score">
                                    <span className="lb-row-score">{entry.totalScore}</span>
                                  </td>
                                </tr>
                              )
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="feedback-page-footer">
        <span className="fb-page-title">Walmart</span>
        <img src={Logo}/>
      </div>
    </div>
  )
}

export default LeaderboardPodium